import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { css, Global, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import Sheet from '@amzn/meridian/sheet';
import { useAnalyticsContext } from 'src/common/metrics';
import { useVerifyOrder } from 'src/hooks/useVerifyOrders';
import { getVerifyOutcome } from 'src/pages/thankYouPage/ThankYouPageVerify';
import { useBagStore } from 'src/store';
import { getFirstOrderItem, getVerifyError } from '../../utils';
import { OrderPlacedView } from './OrderPlacedView';
import { FillColumn, OrderSheetView } from './OrderSheetView';
import { PaymentMethodsView } from './PaymentMethodsView';
const PANEL_CLASS = 'one-click-sheet-panel';
// Hidden, not unmounted, behind the payment picker, so it keeps its last preview
const OrderViewSlot = styled(FillColumn) `
  &[hidden] {
    display: none;
  }
`;
// Capped at 90% of the viewport, with no tap highlight on the controls inside, as on the app's buttons
const SheetContent = styled.div `
  display: flex;
  flex-direction: column;
  max-height: 90dvh;
  overflow-y: auto;
  padding-bottom: env(safe-area-inset-bottom);
  background-color: ${({ theme }) => theme.rio.semantic.color.background.primary};
  -webkit-tap-highlight-color: transparent;
`;
const getFocusedElement = () => (document.activeElement instanceof HTMLElement ? document.activeElement : null);
/** 1-Click Checkout: previews, places and verifies the order in a bottom sheet over the PDP. */
export const OneClickCheckoutSheet = ({ isOpen, placement, onClose }) => {
    const theme = useTheme();
    const rumClient = useAnalyticsContext();
    const bagItem = useBagStore().listBag()[0];
    const [purchaseId, setPurchaseId] = useState();
    const [confirmError, setConfirmError] = useState();
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    // Reset on each opening while rendering, so a previous result never flashes
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            setPurchaseId(undefined);
            setConfirmError(undefined);
            setIsPlacingOrder(false);
            setIsPickerOpen(false);
        }
    }
    const verify = useVerifyOrder({ purchaseId: purchaseId !== null && purchaseId !== void 0 ? purchaseId : '' }, { enabled: !!purchaseId });
    const verifyOutcome = purchaseId
        ? getVerifyOutcome({ orderData: verify.data, isError: verify.isError, isLoading: verify.isLoading })
        : undefined;
    const placedItem = verifyOutcome === 'success' ? getFirstOrderItem(verify.data) : undefined;
    const orderError = confirmError !== null && confirmError !== void 0 ? confirmError : getVerifyError(verifyOutcome);
    const isBusy = isPlacingOrder || verifyOutcome === 'loading';
    useEffect(() => {
        if (!isOpen)
            return;
        rumClient.recordEvent('ONE_CLICK_SHEET_OPEN', { placement });
        // Meridian's scrim does not lock the page behind it
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = overflow;
        };
    }, [isOpen, placement, rumClient]);
    useEffect(() => {
        if (placedItem) {
            rumClient.recordEvent('ORDER_PLACED', { purchaseId, oneClickArm: 'T1', surface: 'ONE_CLICK_SHEET' });
        }
        else if (verifyOutcome && verifyOutcome !== 'loading') {
            rumClient.recordEvent('ONE_CLICK_ERROR_VIEW', { errorType: getVerifyError(verifyOutcome), stage: 'verify' });
        }
    }, [verifyOutcome, placedItem, purchaseId, rumClient]);
    // The Pay with row that opened the payment picker, focused again on Back
    const paymentTriggerRef = useRef(null);
    // Releases focus first: the closing panel turns aria-hidden, which a focused button inside it would block
    const closeSheet = useCallback(() => {
        var _a;
        (_a = getFocusedElement()) === null || _a === void 0 ? void 0 : _a.blur();
        paymentTriggerRef.current = null;
        onClose();
    }, [onClose]);
    const onDismiss = useCallback((reason) => {
        if (isBusy)
            return;
        rumClient.recordEvent('ONE_CLICK_SHEET_DISMISS', { reason });
        closeSheet();
    }, [isBusy, rumClient, closeSheet]);
    // The scrim returns to the PDP, unlike the order placed view's X, which closes the SMA
    const onScrimClose = () => {
        if (!placedItem)
            return onDismiss('scrim');
        rumClient.recordEvent('ONE_CLICK_CONFIRMATION_CLOSE', { reason: 'scrim' });
        closeSheet();
    };
    const onChangePayment = useCallback(() => {
        rumClient.recordEvent('ONE_CLICK_CHANGE_PAYMENT_CLICK', {});
        paymentTriggerRef.current = getFocusedElement();
        setIsPickerOpen(true);
    }, [rumClient]);
    useEffect(() => {
        if (isPickerOpen || !paymentTriggerRef.current)
            return;
        paymentTriggerRef.current.focus();
        paymentTriggerRef.current = null;
    }, [isPickerOpen]);
    const onOrderConfirmed = useCallback((confirmedPurchaseId) => {
        setIsPlacingOrder(false);
        setPurchaseId(confirmedPurchaseId);
    }, []);
    const onError = useCallback((errorType) => {
        setIsPlacingOrder(false);
        setConfirmError(errorType);
        rumClient.recordEvent('ONE_CLICK_ERROR_VIEW', { errorType, stage: 'confirm' });
    }, [rumClient]);
    return (_jsxs(_Fragment, { children: [_jsx(Global, { styles: css `
          div.${PANEL_CLASS} {
            top: 100%;
            bottom: auto;
            transform: translateY(-100%);
            border-radius: ${theme.rio.semantic.size.radius.extraLarge} ${theme.rio.semantic.size.radius.extraLarge} 0 0;
            box-shadow: 0 0 ${theme.rio.base.size[14]} ${theme.rio.semantic.color.shadow.medium};
            animation: one-click-sheet-up 250ms ease-out;
            @media (prefers-reduced-motion: reduce) {
              animation: none;
            }
          }
          div.${PANEL_CLASS} + div {
            background-color: ${theme.rio.semantic.color.shadow.dark};
          }
          @keyframes one-click-sheet-up {
            from {
              transform: translateY(0);
            }
          }
        ` }), _jsx(Sheet, { type: "overlay", side: "bottom", open: isOpen, onClose: onScrimClose, spacingInset: "none", className: PANEL_CLASS, motionDuration: isOpen ? 1 : undefined, children: _jsxs(SheetContent, { "data-testid": "one-click-checkout-sheet", children: [placedItem && bagItem && verify.data && (_jsx(OrderPlacedView, { item: placedItem, shippingAddress: verify.data.shippingAddress, productImageUrl: bagItem.productImageUrl })), !placedItem && bagItem && isPickerOpen && _jsx(PaymentMethodsView, { onBack: () => setIsPickerOpen(false) }), !placedItem && bagItem && (_jsx(OrderViewSlot, { hidden: isPickerOpen, "data-testid": "one-click-order-slot", children: _jsx(OrderSheetView, { onDismiss: onDismiss, onOrderConfirmed: onOrderConfirmed, onError: onError, onPlaceOrder: () => setIsPlacingOrder(true), onChangePayment: onChangePayment, isBusy: isBusy, orderError: orderError }) }))] }) })] }));
};
